import React, { useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '../utils/cn';
import { ilmsInputClass } from './ILMSForm';
import type { RefOption } from '../features/common/referenceData';

/**
 * Multi-select animal picker for group entries (e.g. a Milk Session recorded
 * for several animals milked together as one combined figure, ಕನ್ನಡ: ಗುಂಪು
 * ದಾಖಲೆ) — same search-to-filter idea as ILMSAnimalPicker, but keeps a list
 * of selections (checkboxes) shown as removable chips, instead of picking one
 * and closing.
 */
export function ILMSAnimalMultiPicker({
  id,
  value,
  onChange,
  options,
  error,
  placeholder = 'Search animals to add…',
}: {
  id?: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: RefOption[];
  error?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOptions = value.map((v) => options.find((o) => o.value === v)).filter((o): o is RefOption => !!o);
  const filtered = (query.trim() ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase())) : options);

  React.useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const toggle = (animalId: string) => {
    onChange(value.includes(animalId) ? value.filter((v) => v !== animalId) : [...value, animalId]);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={id}
        onClick={() => setOpen((v) => !v)}
        className={cn(ilmsInputClass(error), 'flex items-center justify-between text-left min-h-11 h-auto py-2')}
      >
        {selectedOptions.length === 0 ? (
          <span className="text-ink-400">Select 2 or more animals…</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {selectedOptions.map((o) => (
              <span
                key={o.value}
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); toggle(o.value); }}
                className="inline-flex items-center gap-1 rounded-full bg-brand-green-50 text-brand-green-800 border border-brand-green-100 px-2 py-0.5 text-helper-text"
              >
                {o.label} <X className="h-3 w-3" />
              </span>
            ))}
          </span>
        )}
        <ChevronDown className="h-4 w-4 shrink-0 text-ink-400 ml-auto" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-ink-300/30 bg-white shadow-popover overflow-hidden">
          <div className="p-2 border-b border-ink-300/10 flex items-center gap-2">
            <Search className="h-4 w-4 text-ink-400 shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="flex-1 min-w-0 text-body outline-none"
            />
          </div>
          {filtered.length > 0 && (
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-ink-300/10 text-helper-text">
              <button
                type="button"
                onClick={() => onChange(Array.from(new Set([...value, ...filtered.map((o) => o.value)])))}
                className="text-brand-green-700 hover:underline"
              >
                Select all {filtered.length} shown
              </button>
              <button
                type="button"
                onClick={() => onChange(value.filter((v) => !filtered.some((o) => o.value === v)))}
                className="text-ink-400 hover:underline"
              >
                Clear
              </button>
            </div>
          )}
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-secondary text-ink-400 px-3 py-3">No matching animals.</p>
            ) : (
              filtered.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 w-full text-left px-3 py-2.5 text-body hover:bg-brand-green-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={value.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                    className="h-4 w-4 rounded border-ink-300/50 text-brand-green-700 focus-visible:outline-2 focus-visible:outline-brand-green-500"
                  />
                  {opt.label}
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
